import React, { useState, useRef, useEffect, useCallback } from "react";
import addIcon from "../../assets/images/icons/addIcon.png";
import filterIcon from "../../assets/images/icons/filterIcon.svg";
import searchIcon from "../../assets/images/icons/search.png";
import DataTable from "../../components/common/DataTable/DataTable";
import FilterCoreModal from "../../components/common/FilterCoreModal/FilterCoreModal";
import FilterForm from "../../components/MyTasks/FilterForm/FilterForm";
import { nanoid } from "@reduxjs/toolkit";
import editIcon from "../../assets/images/icons/edit.png";
import IncidentReportingModal from "../../components/Incidents/IncidentReportingModal/IncidentReportingModal";
import DataForm from "../../components/common/DataForm/DataForm";
import { Loader } from "../../components/common/Loader/Loader";
import toast, { Toaster } from "react-hot-toast";
import deleteIcon from "../../assets/images/icons/Delete.svg";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import {
  useGetUsersQuery,
  useGetHierarchyQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetRolesQuery,
} from "./manageUsersSlice";
import { useGetIncidentGroupsQuery } from "../Incidents/IncidentSlice";
import { sanitizeInputValue } from "../../utils/commonMethods";

const ManageUsers = () => {
  let gridRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [columns, setColumns] = useState("");
  const [userData, setUserData] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [roleData, setRoleData] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [incidentGroupsData, setIncidentGroupsData] = useState([]);

  const [page, setPage] = useState(1);

  const [query, setQuery] = useState("");
  const [orderBy, setorderBy] = useState("");
  const [orderByType, setOrderByType] = useState("Desc");
  const [isAscending, setIsAscending] = useState(false);

  const [formStructure, setFormStructure] = useState({
    pages: [
      {
        name: "page1",
        elements: [
          {
            type: "text",
            name: "firstName",
            title: "First Name",
            hideNumber: true,
            isRequired: true,
            placeholder: "Enter First Name",
          },
          {
            type: "text",
            name: "lastName",
            startWithNewLine: false,
            title: "Last Name",
            hideNumber: true,
            isRequired: true,
            placeholder: "Enter Last Name",
          },
          {
            type: "paneldynamic",
            name: "roles",
            title: "Roles",
            state: "collapsed",
            hideNumber: true,
            isRequired: true,
            templateElements: [
              {
                type: "dropdown",
                name: "id",
                title: "Role",
                isRequired: true,
              },
              {
                type: "dropdown",
                name: "incidentGroupId",
                startWithNewLine: false,
                title: "Group",
              },
              {
                type: "dropdown",
                name: "directorateId",
                startWithNewLine: false,
                title: "Directorate",
              },
              {
                type: "dropdown",
                name: "reportingStreamId",
                startWithNewLine: false,
                title: "Reporting Stream",
                visibleIf: "{panel.directorateId} notempty",
              },
              {
                type: "dropdown",
                name: "departmentId",
                startWithNewLine: false,
                title: "Department",
                visibleIf: "{panel.reportingStreamId} notempty ",
              },
              {
                type: "dropdown",
                name: "subDepartmentId",
                startWithNewLine: false,
                title: "Sub Department",
                visibleIf: "{panel.departmentId} notempty ",
              },
              {
                type: "dropdown",
                name: "areaId",
                startWithNewLine: false,
                title: "Area",
                visibleIf: "{panel.subDepartmentId} notempty ",
              },
              {
                type: "dropdown",
                name: "costCenterId",
                startWithNewLine: false,
                title: "Cost Center",
                visibleIf: "{panel.subDepartmentId} notempty ",
              },
            ],
            panelCount: 1,
            minPanelCount: 1,
            panelAddText: "Add Role",
            panelRemoveText: "Remove Role",
          },
          {
            type: "text",
            name: "email",
            title: "Email",
            hideNumber: true,
            isRequired: true,
            inputType: "email",
          },
          {
            type: "boolean",
            name: "changePassword",
            title: "Set Password",
            defaultValue: "false",
            hideNumber: true,
          },
          {
            type: "text",
            name: "password",
            title: "Password",
            hideNumber: true,
            isRequired: true,
            visibleIf: "{changePassword} = true",
            inputType: "password",
          },
        ],
      },
    ],
    widthMode: "responsive",
  });

  const {
    data: users,
    isLoading: usersIsLoading,
    isFetching: userIsFetching,
  } = useGetUsersQuery({
    email: "",
    name: "",
    pageSize: "",
    query,
    page,
    orderBy,
    orderByType,
  });

  const { data: incidentGroups } = useGetIncidentGroupsQuery();

  const { data: roles } = useGetRolesQuery();

  const { data: hierarchyData } = useGetHierarchyQuery();

  const generateColumns = useCallback((data) => {
    if (data.length <= 0) {
      return;
    }

    const optionsConfig = {
      key: nanoid(),
      field: "options",
      headerText: "Options",
      template: (props) => (
        <div>
          <button
            className="e-btn e-flat option-btn"
            onClick={() => {
              setSelectedData(props);
              setShowModal(true);
            }}
          >
            <img src={deleteIcon} width={30} alt="delete icon" />
          </button>
          <button
            className="e-btn e-flat option-btn"
            onClick={() => updateHandleClick(props)}
          >
            <img src={editIcon} width={30} alt="edit icon" />
          </button>
        </div>
      ),
      width: "200px",
    };

    const columns = Object.keys(data[0])
      .map((field) => {
        if (field === "id") {
          return null;
        }

        let columnConfig = {
          key: nanoid(),
          field,
          headerText: field
            .replace(/([A-Z])/g, " $1")
            .trim()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          alignment: "left",
          template: null,
          width: "180px",
        };

        if (field === "lastName") {
          return null;
        }
        if (field === "firstName") {
          return null;
        }
        if (field === "password") {
          return null;
        }
        if (field === "pageSettings") {
          return null;
        }
        if (field === "name") {
          columnConfig.template = (props) => {
            return <p className="m-0 fw-bold">{props[field]}</p>;
          };
        }

        if (field === "roles") {
          columnConfig.template = (props) => {
            return (
              <div>
                {props[field].length > 0
                  ? props[field].map((role) => (
                      <span
                        className="me-1 badge rounded-pill bg-success"
                        key={role.id}
                      >
                        {role.description}
                      </span>
                    ))
                  : "—"}
              </div>
            );
          };
        }

        return columnConfig;
      })
      .filter(Boolean);

    columns.splice(columns.length, 0, optionsConfig);
    return columns;
  }, []);

  useEffect(() => {
    if (users) {
      const data = users.ids.map((id) => {
        return users.entities[id];
      });
      setUserData(data);
      setColumns(generateColumns(data));
    }
  }, [users, generateColumns]);

  useEffect(() => {
    if (roles) {
      const data = roles.ids.map((id) => {
        return roles.entities[id];
      });
      setRoleData(data);
    }
  }, [roles]);

  useEffect(() => {
    if (incidentGroups) {
      const data = incidentGroups.ids.map((id) => {
        return incidentGroups.entities[id];
      });
      setIncidentGroupsData(data);
    }
  }, [incidentGroups]);

  useEffect(() => {
    if (hierarchyData) {
      setHierarchy(hierarchyData);
    }
  }, [hierarchyData]);

  useEffect(() => {
    setUserRoles({ roleData, hierarchy, incidentGroupsData });
  }, [roleData, hierarchy, incidentGroupsData]);

  const filterHierarchy = (
    directorateId,
    reportingStreamId,
    departmentId,
    subDepartmentId
  ) => {
    const directorate = hierarchy.find((dir) => dir.id === directorateId);
    if (!directorate) return {};

    const reportingStream = directorate.reportingStreams?.find(
      (rs) => rs.id === reportingStreamId
    );
    if (!reportingStream) return {};

    const department = reportingStream.departments?.find(
      (dep) => dep.id === departmentId
    );
    if (!department) return {};

    const subDepartment = department.subDepartments?.find(
      (sub) => sub.id === subDepartmentId
    );
    return {
      reportingStreams: directorate.reportingStreams || [],
      departments: reportingStream.departments || [],
      subDepartments: department.subDepartments || [],
      costCenters: subDepartment?.costCenters || [],
      areas: subDepartment?.areas || [],
    };
  };

  const handleSorting = (args) => {
    if (args.requestType === "sorting") {
      const columnName = args.columnName;
      // const direction = args.direction;

      switch (columnName) {
        case "name":
          setorderBy("Name");
          break;
        case "email":
          setorderBy("Email");
          break;
        case "roles":
          setorderBy("Roles");
          break;
        default:
          setorderBy("");
      }

      if (isAscending) {
        setOrderByType("Asc");
        setIsAscending((prev) => !prev);
      } else {
        setOrderByType("Desc");
        setIsAscending((prev) => !prev);
      }
      args.cancel = true;
    }
  };

  const updateChoices = (
    panel,
    dropdownName,
    items,
    keyField = "key",
    descField = "description"
  ) => {
    const dropdown = panel.getQuestionByName(dropdownName);
    dropdown.choices = items.map((item) => ({
      value: item.id,
      text: `${item[keyField]} | ${item[descField]}`,
    }));
  };

  const handleAfterPanelRender = (sender, options) => {
    const panel = options.panel;

    const directorateId = panel.getQuestionByName("directorateId").value;
    const reportingStreamId =
      panel.getQuestionByName("reportingStreamId")?.value;
    const departmentId = panel.getQuestionByName("departmentId")?.value;
    const subDepartmentId = panel.getQuestionByName("subDepartmentId")?.value;

    const filteredData = filterHierarchy(
      directorateId,
      reportingStreamId,
      departmentId,
      subDepartmentId
    );

    if (filteredData.reportingStreams) {
      updateChoices(panel, "reportingStreamId", filteredData.reportingStreams);
    }
    if (filteredData.departments) {
      updateChoices(panel, "departmentId", filteredData.departments);
    }
    if (filteredData.subDepartments) {
      updateChoices(panel, "subDepartmentId", filteredData.subDepartments);
    }
    if (filteredData.costCenters) {
      updateChoices(panel, "costCenterId", filteredData.costCenters);
    }
    if (filteredData.areas) {
      updateChoices(panel, "areaId", filteredData.areas);
    }
  };

  const [createUser, { isLoading: createUserIsLoading }] =
    useCreateUserMutation();

  const [updateUser, { isLoading: updateUserIsLoading }] =
    useUpdateUserMutation();

  const [deleteUser, { isLoading: deleteUserIsLoading }] =
    useDeleteUserMutation();

  const closeModal = () => {
    setModalOpen(false);
    // resetForm();
  };

  const openModal = () => {
    setIsUpdate(false);
    setModalOpen(true);
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const updateHandleClick = (props) => {
    const { id, firstName, lastName, email, password, roles } = props;
    const rolesData = roles.map((r) => ({
      ...r,
      incidentGroupId: r?.incidentGroup?.id,
      directorateId: r?.directorate?.id,
      reportingStreamId: r?.reportingStream?.id,
      departmentId: r?.department?.id,
      subDepartmentId: r?.subDepartment?.id,
      costCenterId: r?.costCenter?.id,
      areaId: r?.area?.id,
    }));

    const data = {
      id,
      firstName,
      lastName,
      email,
      password,
      roles: rolesData,
    };

    setIsUpdate(true);
    setSelectedData(data);
    setModalOpen(true);
  };

  const setUserRoles = (data) => {
    const updatedFormStructure = { ...formStructure };
    const roleDropdown = updatedFormStructure.pages[0].elements.find(
      (element) => element.name === "roles"
    );

    if (roleDropdown) {
      roleDropdown.templateElements[0].choices = data.roleData.map((role) => ({
        value: role.id,
        text: role.description,
      }));
      roleDropdown.templateElements[1].choices = data.incidentGroupsData.map(
        (group) => ({
          value: group.id,
          text: group.description,
        })
      );
      roleDropdown.templateElements[2].choices = data.hierarchy.map(
        (group) => ({
          value: group.id,
          text: group.description,
        })
      );
    }

    setFormStructure(updatedFormStructure);
  };

  const handlePanelChange = (sender, options) => {
    const questionName = options.name;
    const newValue = options.value;

    if (questionName === "directorateId") {
      const panel = options.panel;
      const reportingStreamDropdown =
        panel.getQuestionByName("reportingStreamId");
      const reportingStreams = hierarchy?.find(
        (directorate) => directorate?.id === newValue
      )?.reportingStreams;

      reportingStreamDropdown.choices = reportingStreams.map((rs) => ({
        value: rs.id,
        text: `${rs.key} | ${rs.description}`,
      }));
    }

    if (questionName === "reportingStreamId") {
      const panel = options.panel;
      const directorateId = panel.getQuestionByName("directorateId").value;
      const departmentDropdown = panel.getQuestionByName("departmentId");
      const departments = hierarchy
        ?.find((directorate) => directorate?.id === directorateId)
        ?.reportingStreams?.find((rs) => rs.id === newValue)?.departments;

      departmentDropdown.choices = departments?.map((dep) => ({
        value: dep.id,
        text: `${dep.key} | ${dep.description}`,
      }));
    }

    if (questionName === "departmentId") {
      const panel = options.panel;
      const directorateId = panel.getQuestionByName("directorateId").value;
      const reportingStreamId =
        panel.getQuestionByName("reportingStreamId").value;
      const subDepartmentDropdown = panel.getQuestionByName("subDepartmentId");
      const subDepartments = hierarchy
        ?.find((directorate) => directorate?.id === directorateId)
        ?.reportingStreams?.find((rs) => rs.id === reportingStreamId)
        ?.departments?.find((dep) => dep.id === newValue)?.subDepartments;

      subDepartmentDropdown.choices = subDepartments.map((dep) => ({
        value: dep.id,
        text: `${dep.key} | ${dep.description}`,
      }));
    }

    if (questionName === "subDepartmentId") {
      const panel = options.panel;
      const directorateId = panel.getQuestionByName("directorateId").value;
      const reportingStreamId =
        panel.getQuestionByName("reportingStreamId").value;
      const departmentId = panel.getQuestionByName("departmentId").value;

      const costCentersDropdown = panel.getQuestionByName("costCenterId");
      const areasDropdown = panel.getQuestionByName("areaId");
      const data = hierarchy
        ?.find((directorate) => directorate?.id === directorateId)
        ?.reportingStreams?.find((rs) => rs.id === reportingStreamId)
        ?.departments?.find((dep) => dep.id === departmentId)
        ?.subDepartments?.find((subdep) => subdep.id === newValue);

      costCentersDropdown.choices = data?.costCenters?.map((cc) => ({
        value: cc.id,
        text: `${cc.key} | ${cc.description}`,
      }));
      areasDropdown.choices = data?.areas?.map((area) => ({
        value: area.id,
        text: `${area.key} | ${area.description}`,
      }));
    }
  };

  const handleSave = async (formData) => {
    try {
      if (!isUpdate) {
        await createUser({
          ...formData,
          password: formData.password ? formData.password : "",
        }).unwrap();
        toast.success("User created successfully");
      } else {
        await updateUser({ data: formData, id: selectedData.id }).unwrap();
        toast.success("User updated successfully");
      }
      closeModal();
    } catch (error) {
      toast.error(error.data.Message);
    }
  };
  const handleDelete = async () => {
    try {
      await deleteUser(selectedData.id).unwrap();
      toast.success("User deleted successfully");
      setShowModal(false);
    } catch (error) {
      toast.error(error.data.Message);
    }
  };

  const handleNewUser = () => {
    openModal();
  };

  return (
    <div className="task-libarary">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0b463d",
            color: "#ffffff",
          },
          success: {
            iconTheme: {
              secondary: "#ffffff",
            },
          },
        }}
      />
      <div className="row mb-1 justify-content-between align-items-center">
        <div className="col-auto">
          <h4 className="p-2 m-0">Manage Users</h4>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-green btn-icon px-5"
            onClick={handleNewUser}
          >
            <span className="me-2"> New User</span>
            <img src={addIcon} width={22} alt="" />
          </button>
        </div>
      </div>
      <div className="card custom-card incident-details-card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col h-100">
              <div className="card custom-card img-card banner">
                <div>
                  <div className="card-body custom-card-body">
                    <p className="fs-2">Manage Users</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row justify-content-between align-items-center mb-3">
            <div className="col-auto">
              <h6>User List</h6>
            </div>
            <div className="col-auto">
              <div className="d-flex align-items-center">
                <div className="input-group">
                  <label
                    htmlFor="searchUser"
                    className="input-group-text search-icon-container"
                  >
                    <img
                      src={searchIcon}
                      alt="Search Icon"
                      className="icon-image"
                      width={12}
                    />
                  </label>
                  <input
                    id="searchUser"
                    type="text"
                    className="form-control search"
                    placeholder="Search Users"
                    onKeyUp={(e) => {
                      const inputValue = sanitizeInputValue(e.target.value);
                      setQuery(inputValue);
                    }}
                  />
                </div>
                <button className="btn btn-filter" onClick={toggleModal}>
                  <img src={filterIcon} width={34} alt="" />
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {userIsFetching && <Loader />}
              {usersIsLoading ? (
                ""
              ) : userData.length > 0 ? (
                <DataTable
                  data={userData}
                  columns={columns}
                  height={"calc(100vh - 555px)"}
                  grid={gridRef}
                  defaultPaging={false}
                  currentPage={page}
                  handlePageChange={(val) => setPage(val)}
                  allowSorting={true}
                  handleSorting={handleSorting}
                />
              ) : (
                "No Users Available..."
              )}
            </div>
          </div>
        </div>
      </div>
      <FilterCoreModal isOpen={modalIsOpen} toggle={toggleModal}>
        <FilterForm />
      </FilterCoreModal>
      <IncidentReportingModal
        isOpen={modalOpen}
        toggle={closeModal}
        title={isUpdate ? "Update User" : "New User"}
        size={"xl"}
      >
        {createUserIsLoading || updateUserIsLoading ? (
          <div className="mb-5">
            <Loader />
          </div>
        ) : (
          <DataForm
            data={formStructure}
            formData={isUpdate ? selectedData : null}
            handleSave={handleSave}
            isUpdate={isUpdate}
            handlePanelChange={handlePanelChange}
            handleAfterPanelRender={
              isUpdate ? handleAfterPanelRender : () => {}
            }
          />
        )}
      </IncidentReportingModal>
      <DeleteConfirmationModal
        type={`user "${selectedData?.firstName}"`}
        showModal={showModal}
        closeModal={setShowModal}
        confirmDelete={handleDelete}
        loading={deleteUserIsLoading}
      />
    </div>
  );
};

export default ManageUsers;
